// ==========================
// Version 2 — components/ErrorBoundary.jsx
// - Adds aria roles/labels for a11y
// - Shows component stack in dev builds
// - Exposes onReset prop (already used by AppShell)
// ==========================
import React from 'react';

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
    this._lastInfo = null;
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // keep the component stack available during dev
    this._lastInfo = info;
    // TODO: send to logging service
    // console.error('ErrorBoundary caught:', error, info);
  }

  handleReset = () => {
    if (this.props.onReset) this.props.onReset();
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={styles.wrap}>
          <div
            style={styles.card}
            role="alertdialog"
            aria-labelledby="eb-title"
            aria-describedby="eb-desc"
          >
            <h2 id="eb-title" style={{ marginTop: 0 }}>Something went wrong</h2>
            <p id="eb-desc" style={{ opacity: 0.85 }}>
              The page crashed while rendering. You can try again, or go back home.
            </p>

            {process.env.NODE_ENV !== 'production' && this.state.error && (
              <pre style={styles.pre}>
                {String(this.state.error?.message || this.state.error)}
              </pre>
            )}
            {process.env.NODE_ENV !== 'production' && this._lastInfo?.componentStack && (
              <pre style={styles.pre}>
                {this._lastInfo.componentStack}
              </pre>
            )}

            <div style={styles.row}>
              <button onClick={this.handleReset} style={styles.btnPrimary}>Try again</button>
              <a href="/" style={styles.btnGhost}>Go Home</a>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const styles = {
  wrap: {
    minHeight: '100vh',
    display: 'grid',
    placeItems: 'center',
    background: '#0A1A33',
    padding: 24,
  },
  card: {
    width: 'min(640px, 100%)',
    background: '#FDFCF9',
    border: '1px solid rgba(201,164,76,0.35)',
    borderRadius: 16,
    padding: 20,
    boxShadow: '0 20px 60px rgba(10,26,51,0.35)',
    color: '#0A1A33',
  },
  pre: {
    background: 'rgba(10,26,51,0.04)',
    border: '1px solid rgba(10,26,51,0.12)',
    borderRadius: 6,
    padding: 12,
    margin: '12px 0',
    maxHeight: 180,
    overflow: 'auto',
    fontSize: 12,
  },
  row: { display: 'flex', gap: 12, marginTop: 8 },
  btnPrimary: {
    background: '#5C1A2B',
    color: '#FDFCF9',
    border: 'none',
    borderRadius: 10,
    padding: '10px 14px',
    cursor: 'pointer',
  },
  btnGhost: {
    border: '1px solid rgba(201,164,76,0.55)',
    borderRadius: 10,
    padding: '9px 14px',
    color: '#0A1A33',
    textDecoration: 'none',
  },
};
// ==========================
// End Version 2 — components/ErrorBoundary.jsx
// ==========================
